import { createStripeCustomer, lookupStripeCustomer, getStripeProducts, getRemainingUnits, createStripePortalSession, getCustomerSettings as fetchCustomerSettings, createSubscriptionCheckoutSession as apiCreateSubscriptionCheckoutSession, finalizeSubscription as apiFinalizeSubscription, getCurrentBillingCycleSummary as apiGetCurrentBillingCycleSummary, getUsageRecords as apiGetUsageRecords } from '../../utils/apiClient';

/**
 * Service for handling Stripe customer operations
 */
class BillingService {
  /**
   * Initializes or retrieves a customer's Stripe account
   * @param {string} email - Customer's email address
   * @returns {Promise<Object>} Customer initialization response
   */
  static async initializeCustomer(email) {
    try {
      // First, try to lookup the customer
      const lookupResponse = await this.lookupCustomer(email);
      if (lookupResponse.success && lookupResponse.exists) {
        return {
          success: true,
          isNewCustomer: false,
          customer: lookupResponse.customer,
          message: 'Customer already exists'
        };
      }
      // If customer doesn't exist, create new customer with trial subscription
      const createResponse = await this.createCustomer(email);
      if (createResponse.success) {
        return {
          success: true,
          isNewCustomer: true,
          customer: {
            id: createResponse.customerId,
            email: createResponse.email,
            created: createResponse.created
          },
          message: createResponse.message
        };
      }
      return createResponse;
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Failed to initialize customer'
      };
    }
  }

  /**
   * Gets remaining units for a customer
   * @returns {Promise<Object>} Remaining units response
   */
  static async getRemainingUnits() {
    try {
      const response = await getRemainingUnits();
      return {
        success: true,
        includedUnitsLimit: response.included_units_limit,
        totalUsage: response.totalUsage ?? response.total_usage ?? 0,
        remainingUnits: response.remainingUnits ?? response.remaining_units ?? 0,
        isOverageActive: response.is_overage_active ?? false,
        planType: response.planType ?? null,
        overagePlanType: response.overagePlanType ?? null,
        overageRatePerHour: response.overage_rate_per_hour ?? null,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: error.message || 'Failed to fetch remaining units'
      };
    }
  }

  /**
   * Creates a new Stripe customer account
   * @returns {Promise<Object>} Customer creation response
   * @throws {Error} If customer already exists or creation fails
   */
  static async createCustomer() {
    try {
      const response = await createStripeCustomer();
      return {
        success: true,
        customerId: response.customer_id,
        email: response.email,
        name: response.name,
        created: response.created,
        message: response.message
      };
    } catch (error) {
      if (error.response?.data?.statusCode === 'BAD_REQUEST') {
        // Extract customer ID from error message if customer exists
        const match = error.response.data.error.match(/ID: (cus_\w+)/);
        const existingCustomerId = match ? match[1] : null;
        return {
          success: false,
          error: 'Customer already exists',
          existingCustomerId,
          message: error.response.data.error
        };
      }
      return {
        success: false,
        error: error.message,
        message: error.message || 'Failed to create customer'
      };
    }
  }

  /**
   * Looks up a Stripe customer by email
   * @param {string} email - Customer's email address
   * @returns {Promise<Object>} Customer lookup response
   * @throws {Error} If lookup fails
   */
  static async lookupCustomer() {
    try {
      const response = await lookupStripeCustomer();
      return {
        success: true,
        exists: response.customer_exists,
        customer: response.customer,
        subscriptions: response.subscriptions,
        totalSubscriptions: response.total_subscriptions
      };
    } catch (error) {
      if (error.response?.data?.statusCode === 'NOT_FOUND') {
        return {
          success: false,
          exists: false,
          message: error.response.data.error
        };
      }
      return {
        success: false,
        error: error.message,
        message: error.message || 'Failed to lookup customer'
      };
    }
  }

  /**
   * Gets Stripe products of a specific type
   * @param {string} type - Product type (default: 'MINUTE')
   * @returns {Promise<Object>} Products response
   * @throws {Error} If fetch fails
   */
  static async getProducts(type = 'MINUTE') {
    try {
      const response = await getStripeProducts(type);
      return {
        success: true,
        products: response.products.map(product => ({
          id: product.id,
          name: product.name,
          description: product.description,
          active: product.active,
          created: product.created,
          prices: product.prices.map(price => ({
            id: price.id,
            nickname: price.nickname,
            amount: price.unit_amount ? price.unit_amount / 100 : 0, // Convert cents to dollars
            currency: price.currency,
            interval: price.recurring?.interval,
            usageType: price.recurring?.usage_type,
            type: price.type
          }))
        })),
        totalProducts: response.total_products,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: error.message || 'Failed to fetch products'
      };
    }
  }

  /**
   * Creates a Stripe customer portal session and returns the portal URL
   * @param {string} returnUrl - The URL to return to after managing payment methods
   * @param {string} section - Optional section for the portal session
   * @returns {Promise<{status: string, url: string}>}
   */
  static async createPortalSession(returnUrl, section) {
    try {
      return await createStripePortalSession(returnUrl, section);
    } catch (error) {
      return { status: 'ERROR', message: error.message };
    }
  }

  /**
   * Gets customer billing settings (plan, payment method, billing info)
   * @param {string} email - Customer's email address
   * @returns {Promise<Object>} The customer settings response
   */
  static async getCustomerSettings(email) {
    try {
      const response = await fetchCustomerSettings(email);
      const { plan, paymentMethod, billingInfo } = response;
      return {
        success: true,
        plan,
        paymentMethod,
        billingInfo
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: error.message || 'Failed to fetch customer settings'
      };
    }
  }

  /**
   * Creates a Stripe subscription checkout session
   * @param {string} productId
   * @param {string} successUrl
   * @param {string} cancelUrl
   * @returns {Promise<Object>} The checkout session response
   */
  static async createSubscriptionCheckoutSession(productId, successUrl, cancelUrl) {
    try {
      return await apiCreateSubscriptionCheckoutSession(productId, successUrl, cancelUrl);
    } catch (error) {
      return { status: 'ERROR', message: error.message };
    }
  }

  /**
   * Finalizes a subscription after successful Stripe checkout
   * @param {string} sessionId - The Stripe checkout session ID
   * @returns {Promise<Object>} The finalization response
   */
  static async finalizeSubscription(sessionId) {
    try {
      return await apiFinalizeSubscription(sessionId);
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: error.message || 'Failed to finalize subscription'
      };
    }
  }

  /**
   * Gets current billing cycle usage summary
   * @returns {Promise<Object>} The current billing cycle usage summary response
   */
  static async getCurrentBillingCycleSummary() {
    try {
      const response = await apiGetCurrentBillingCycleSummary();
      return {
        success: true,
        customerId: response.customerId,
        startDate: response.startDate,
        endDate: response.endDate,
        summaryByType: response.summaryByType || [],
        totalUnits: response.totalUnits || 0,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: error.message || 'Failed to fetch current billing cycle summary'
      };
    }
  }

  /**
   * Gets usage records
   * @returns {Promise<Object>} The usage records response
   */
  static async getUsageRecords() {
    try {
      const response = await apiGetUsageRecords();
      return {
        success: true,
        records: response || [],
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: error.message || 'Failed to fetch usage records',
        records: [],
      };
    }
  }

}

export default BillingService; 